import React, { useState } from "react";
import { Helmet } from "react-helmet";

import {
  FaUser,
  FaCalendarCheck,
  FaClock,
  FaTrophy,
  FaBell,
  FaCalendarAlt,
  FaBook,
  FaClipboardList,
  FaAward,
  FaBookReader,
  FaTasks,
  FaChartBar,
  FaFileAlt,
  FaDollarSign,
  FaBookOpen,
  FaEnvelopeOpenText,
} from "react-icons/fa";

import { COLORS } from "../constants/colors";
import ERPModal from "../Components/ERPModal.js";

const StudentPanel = () => {
  const [isModalOpen, setIsModalOpen] =
    useState(false);

  const features = [
    {
      title: "Profile",
      description:
        "View your student profile.",
      icon: <FaUser />,
    },

    {
      title: "Attendance",
      description:
        "Track your attendance records.",
      icon: <FaCalendarCheck />,
    },

    {
      title: "Timetable",
      description:
        "View your class timetable.",
      icon: <FaClock />,
    },

    {
      title: "Student Achievements",
      description:
        "View your personal achievements.",
      icon: <FaTrophy />,
    },

    {
      title: "Notifications",
      description:
        "Stay updated with important announcements.",
      icon: <FaBell />,
    },

    {
      title: "Events",
      description:
        "Keep track of upcoming school events.",
      icon: <FaCalendarAlt />,
    },

    {
      title: "Homework",
      description:
        "Access and complete your homework assignments.",
      icon: <FaBook />,
    },

    {
      title: "Test Schedule",
      description:
        "Check your upcoming test dates.",
      icon: <FaClipboardList />,
    },

    {
      title: "School Achievements",
      description:
        "Celebrate achievements of your school.",
      icon: <FaAward />,
    },

    {
      title: "Course Material",
      description:
        "Access course materials shared by teachers.",
      icon: <FaBookReader />,
    },

    {
      title: "Assignments",
      description:
        "Check your assignments.",
      icon: <FaTasks />,
    },

    {
      title: "Exam Datesheet",
      description:
        "Stay updated with exam schedules.",
      icon: <FaCalendarAlt />,
    },

    {
      title: "Results",
      description:
        "View your exam results and performance.",
      icon: <FaChartBar />,
    },

    {
      title: "Old Papers",
      description:
        "Access past exam papers for reference.",
      icon: <FaFileAlt />,
    },

    {
      title: "Fee Structure",
      description:
        "Check your fee details and dues.",
      icon: <FaDollarSign />,
    },

    {
      title: "Student Ledger",
      description:
        "View your financial records and payments.",
      icon: <FaBookOpen />,
    },
  ];

  return (
    <>
      {/* SEO HEAD */}
      <Helmet>
        {/* Primary SEO */}
        <title>
          Student Panel Software for Schools |
          Gurukul ERP
        </title>

        <meta
          name="description"
          content="Gurukul ERP Student Panel helps students manage attendance, homework, assignments, results, timetable, fee details, notifications, and academic activities from one secure dashboard."
        />

        <meta
          name="keywords"
          content="
          student panel software,
          school student management system,
          student dashboard software,
          student ERP panel,
          school ERP student panel,
          online student portal,
          student attendance software,
          assignment management system,
          student result portal,
          school management software for students,
          homework management software,
          exam result system,
          student timetable software,
          student portal India,
          Gurukul ERP,
          cloud based school ERP,
          education ERP software,
          student management platform,
          school ERP dashboard,
          student academic portal
          "
        />

        <meta
          name="author"
          content="Gurukul ERP"
        />

        <meta
          name="robots"
          content="index, follow"
        />

        <meta
          name="language"
          content="English"
        />

        <meta
          name="revisit-after"
          content="2 days"
        />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://gurukulerp.in/student-panel"
        />

        {/* Open Graph SEO */}
        <meta
          property="og:title"
          content="Student Panel Software for Schools | Gurukul ERP"
        />

        <meta
          property="og:description"
          content="Modern student panel software for attendance, homework, assignments, results, timetable, fee management, and academic activities."
        />

        <meta
          property="og:image"
          content="https://gurukulerp.in/assets/StudentPanel_Dashboard.jpeg"
        />

        <meta
          property="og:url"
          content="https://gurukulerp.in/student-panel"
        />

        <meta
          property="og:type"
          content="website"
        />

        <meta
          property="og:site_name"
          content="Gurukul ERP"
        />

        {/* Twitter SEO */}
        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="Student Panel Software for Schools | Gurukul ERP"
        />

        <meta
          name="twitter:description"
          content="Powerful student dashboard software for attendance, assignments, homework, exam results, timetable, fee tracking, and academic management."
        />

        <meta
          name="twitter:image"
          content="https://gurukulerp.in/assets/StudentPanel_Dashboard.jpeg"
        />

        {/* Mobile SEO */}
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0"
        />

        <meta
          name="theme-color"
          content="#3A4163"
        />

        {/* Geo SEO */}
        <meta
          name="geo.region"
          content="IN-HR"
        />

        <meta
          name="geo.placename"
          content="Panipat, Haryana"
        />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context":
              "https://schema.org",

            "@type":
              "SoftwareApplication",

            name:
              "Gurukul ERP Student Panel",

            applicationCategory:
              "BusinessApplication",

            operatingSystem: "Web",

            url:
              "https://gurukulerp.in/student-panel",

            image:
              "https://gurukulerp.in/assets/StudentPanel_Dashboard.jpeg",

            description:
              "Student panel software for attendance, homework, assignments, timetable, exam results, fee details, notifications, and academic management.",

            brand: {
              "@type": "Brand",
              name: "Gurukul ERP",
            },

            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "INR",
            },
          })}
        </script>
      </Helmet>

      {/* MAIN CONTAINER */}
      <div
        className="min-h-screen"
        style={{
          background: `linear-gradient(to bottom, ${COLORS.white}, ${COLORS.medium}, ${COLORS.light})`,
          color: COLORS.textPrimary,
        }}
      >
        {/* HERO SECTION */}
        <header className="relative overflow-hidden py-24 text-center">
          <div className="absolute inset-0 bg-gradient-to-b from-[#CBE3EF]/70 via-[#EDF3FB]/60 to-[#FFFFFF]" />

          <div className="relative z-10 max-w-5xl mx-auto px-6">
            <h1 className="text-5xl md:text-6xl font-extrabold mb-6 leading-tight">
              Gurukul ERP Student Panel
            </h1>

            <p className="text-lg md:text-xl text-[#4B5563] max-w-3xl mx-auto mb-10 leading-relaxed">
              All the tools students need for
              attendance, assignments, homework,
              results, timetable, and academic
              success in one modern dashboard.
            </p>

            <div className="flex justify-center gap-6 flex-wrap">
              <button
                onClick={() =>
                  setIsModalOpen(true)
                }
                style={{
                  backgroundColor:
                    COLORS.primary,
                }}
                className="text-white px-10 py-4 rounded-xl font-semibold shadow-md hover:bg-[#3A4163] hover:scale-105 transition-transform duration-300"
              >
                Try Student Panel Demo
              </button>
            </div>
          </div>

          <div className="absolute w-72 h-72 bg-[#5AA8D6]/20 rounded-full blur-3xl top-20 left-16" />

          <div className="absolute w-80 h-80 bg-[#3A4163]/10 rounded-full blur-3xl bottom-10 right-20" />
        </header>

        {/* ABOUT SECTION */}
        <section className="flex flex-col lg:flex-row items-center py-16 px-8">
          <div className="lg:w-1/2 flex justify-center mb-8 lg:mb-0">
            <img
              src="/assets/StudentPanel_Dashboard.jpeg"
              alt="Student Panel Dashboard Software"
              className="rounded-2xl shadow-2xl w-4/5 transform hover:scale-105 transition-transform"
            />
          </div>

          <div className="lg:w-1/2 lg:pl-12 text-center lg:text-left">
            <h2
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                color:
                  COLORS.textPrimary,
              }}
            >
              About Gurukul Student Panel
            </h2>

            <p
              className="text-lg leading-relaxed"
              style={{
                color:
                  COLORS.textSecondary,
              }}
            >
              The Gurukul ERP Student Panel is
              designed to simplify the academic
              experience for students. It
              provides essential tools for
              managing attendance, assignments,
              homework, results, fee records,
              notifications, and school
              activities from one secure
              platform.
            </p>
          </div>
        </section>

        {/* FEATURES SECTION */}
        <section
          className="py-20"
          style={{
            background: `linear-gradient(to bottom, ${COLORS.background}, ${COLORS.primaryLight})`,
          }}
        >
          <div className="container mx-auto px-6">
            <h2
              className="text-4xl font-bold text-center mb-14"
              style={{
                color:
                  COLORS.textPrimary,
              }}
            >
              Key Features of Student Panel
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
              {features.map(
                (feature, index) => (
                  <div
                    key={index}
                    className="group relative [perspective:1200px]"
                  >
                    <div className="relative h-40 w-full transition-all duration-700 ease-out [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]">
                      {/* FRONT SIDE */}
                      <div
                        className="absolute inset-0 rounded-2xl shadow-lg flex flex-col items-center justify-center text-white text-center p-2 [backface-visibility:hidden] transition-all duration-500 group-hover:shadow-2xl group-hover:scale-105"
                        style={{
                          background: `linear-gradient(145deg, ${COLORS.primary}, ${COLORS.primaryDark})`,
                        }}
                      >
                        <div className="text-2xl mb-3 drop-shadow-lg">
                          {feature.icon}
                        </div>

                        <h3 className="text-md font-bold">
                          {feature.title}
                        </h3>
                      </div>

                      {/* BACK SIDE */}
                      <div
                        className="absolute inset-0 rounded-2xl bg-white shadow-xl flex flex-col items-center justify-center text-center p-5 [transform:rotateY(180deg)] [backface-visibility:hidden] transition-all duration-700"
                        style={{
                          color:
                            COLORS.textPrimary,
                        }}
                      >
                        <p className="text-md leading-relaxed font-medium">
                          {
                            feature.description
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </section>

        {/* REQUEST DEMO */}
        <section className="mt-15 pt-10 pb-10 text-center bg-gradient-to-b from-[#CBE3EF]/20 via-[#EDF3FB]/60 to-[#FFFFFF]">
          <div className="max-w-3xl mx-auto bg-white p-10 rounded-2xl shadow-2xl">
            <div className="flex justify-center mb-6">
              <FaEnvelopeOpenText
                className="text-5xl"
                style={{
                  color:
                    COLORS.primary,
                }}
              />
            </div>

            <h2
              className="text-3xl font-bold mb-4"
              style={{
                color:
                  COLORS.textPrimary,
              }}
            >
              Request Demo Credentials
            </h2>

            <p
              className="mb-8 text-lg"
              style={{
                color:
                  COLORS.textSecondary,
              }}
            >
              Get demo login credentials to
              explore the features of our
              Student Panel dashboard.
            </p>

            <div className="flex flex-col md:flex-row justify-center gap-6">
              <button
                onClick={() =>
                  setIsModalOpen(true)
                }
                className="text-white px-10 py-4 rounded-lg font-semibold shadow-md transition-all"
                style={{
                  backgroundColor:
                    COLORS.primary,
                }}
              >
                Request Demo
              </button>

              <a
                href="#"
                className="px-10 py-4 rounded-lg font-semibold shadow-md transition-all"
                style={{
                  backgroundColor:
                    COLORS.medium,
                  color:
                    COLORS.textPrimary,
                }}
              >
                Try Demo
              </a>
            </div>
          </div>
        </section>

        {/* SEO CONTENT */}
        <section className="py-20 px-6 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center">
              Why Students Prefer Gurukul ERP
              Student Panel
            </h2>

            <p className="text-lg leading-9 text-gray-700 mb-6">
              Gurukul ERP Student Panel helps
              students manage academic tasks
              efficiently through an easy-to-use
              online dashboard. Students can
              track attendance, assignments,
              homework, exam schedules, fee
              details, and academic performance
              from one secure platform.
            </p>

            <p className="text-lg leading-9 text-gray-700 mb-6">
              The student dashboard improves
              communication between schools,
              teachers, parents, and students
              while reducing manual work.
              Students can access study
              materials, notifications, old
              papers, and results anytime from
              any device.
            </p>

            <p className="text-lg leading-9 text-gray-700">
              Gurukul ERP is a modern cloud
              based school ERP software designed
              for schools, coaching institutes,
              and educational organizations
              looking to digitize student
              management and academic
              operations.
            </p>
          </div>
        </section>

        {/* MODAL */}
        <ERPModal
          isOpen={isModalOpen}
          onClose={() =>
            setIsModalOpen(false)
          }
        />
      </div>
    </>
  );
};

export default StudentPanel;